<template>
  <FloatingCard class="stack" :feeling="feeling">
    <DocumentsSelectionList
      v-model="selectedDocuments"
      :apiToken="this.documentManagerToken"
      :container="this.documentManagerContainer"
      :imported="importedDocuments"
      :failed="failedDocuments"
      @status-change="updateStatus"
    />
    <ButtonComponent
      v-if="documentsForImport.length > 0"
      :label="buttonLabel"
      @click="importDocuments"
    />
    <template v-if="status === 'documents-imported' || status === 'something-wrong'">
      <div>
        <p>{{ result }}</p>
      </div>
    </template>
  </FloatingCard>
</template>

<script>
import ButtonComponent from '@/components/ButtonComponent';
import FloatingCard from '@/components/FloatingCard';
import DocumentsSelectionList from '@/components/DocumentsSelectionList';

import Flexi from '@/mixins/Flexi';
import DocumentManager from '@/mixins/DocumentManager';

import Mapping from '@/config/mapping';

export default {
  name: 'ImportCard',
  components: {
    ButtonComponent,
    FloatingCard,
    DocumentsSelectionList,
  },
  mixins: [
    Flexi,
    DocumentManager,
  ],
  props: {
    documentManagerToken: {
      type: String,
    },
    documentManagerContainer: {
      type: Object,
    },
    flexiUrl: {
      type: String,
    },
    flexiToken: {
      type: String,
    },
    flexiCompany: {
      type: String,
    },
  },
  data() {
    return {
      status: null,
      documents: [],
      selectedDocuments: [],
      importResults: {},
      result: null,
    };
  },
  computed: {
    feeling() {
      switch (this.status) {
        case 'listing-documents':
          return 'pending';
        case 'container-access-denied':
          return 'negative';
        case 'documents-listed':
          return null;
        case 'importing-documents':
          return 'pending';
        case 'documents-imported':
          return 'positive';
        case 'something-wrong':
          return 'negative';
        default:
          return null;
      }
    },
    buttonLabel() {
      const selectedCount = this.selectedDocuments.length;
      const totalCount = this.documents.length;
      const notImportedYetCount = this.documentsForImport.length;
      const count = selectedCount || notImportedYetCount;
      return `Importovat ${this.pluralizeDocumentsString(count, (count === totalCount) ? totalCount : null)}`;
    },
    documentsForImport() {
      return this.documents
        .map((document) => (document.id))
        .filter((document) => !Object.keys(this.importedDocuments).includes(document));
    },
    importedDocuments() {
      return Object.fromEntries(
        Object.entries(this.importResults)
          .filter((item) => (item[1].invoice.success)),
      );
    },
    failedDocuments() {
      return Object.fromEntries(
        Object.entries(this.importResults)
          .filter((item) => (!item[1].invoice.success)),
      );
    },
  },
  methods: {
    updateStatus(payload) {
      this.status = payload.status;
      this.documents = payload.data.documents;
    },
    updateSelectedDocuments(payload) {
      this.selectedDocuments = payload;
    },
    async retrieveDocumentInformation(documentID) {
      return this.$_DocumentManager_retrieveDocumentInformation({
        token: this.documentManagerToken,
        document: { id: documentID },
      });
    },
    async addEntry({ entry, evidence }) {
      return this.$_Flexi_addEntry({
        apiURL: this.flexiUrl,
        token: this.flexiToken,
        company: this.flexiCompany,
        entry,
        evidence,
      });
    },
    async importDocuments() {
      try {
        this.status = 'importing-documents';
        const documents = this.selectedDocuments.length > 0
          ? this.selectedDocuments
          : this.documentsForImport;
        const importedDocuments = [];

        const importDocument = async (documentId) => {
          const information = await this.retrieveDocumentInformation(documentId);
          const importedDocument = this.documents
            .filter((document) => (document.id === documentId))[0];

          const mappedCompanyData = Object.fromEntries(
            Object.entries(
              await Mapping({
                document: importedDocument,
                information: (tag) => (information[tag]),
                evidence: 'adresar',
                variant: `${new URL(this.flexiUrl).hostname}+${this.flexiCompany}`,
              }),
            ).filter((item) => (item[1] != null)),
          );
          const company = await this.addEntry({ entry: mappedCompanyData, evidence: 'adresar' });

          if (parseInt(company.data.winstrom.stats.created, 10) === 1) {
            this.importResults = {
              ...this.importResults,
              [documentId]: {
                ...this.importResults[documentId],
                company: { created: true },
              },
            };
          }

          const mappedDocumentData = Object.fromEntries(
            Object.entries(
              await Mapping({
                document: importedDocument,
                information: (tag) => (information[tag]),
                evidence: 'faktura-prijata',
                variant: `${new URL(this.flexiUrl).hostname}+${this.flexiCompany}`,
              }),
            ).filter((item) => (item[1] != null)),
          );

          try {
            const importResult = await this.addEntry({ entry: mappedDocumentData, evidence: 'faktura-prijata' });
            importedDocuments.push(documentId);
            this.importResults = {
              ...this.importResults,
              [documentId]: {
                ...this.importResults[documentId],
                invoice: { success: true },
              },
            };
            this.selectedDocuments = this.selectedDocuments
              .filter((document) => document !== documentId);
            try {
              await this.$_Flexi_addAttachment({
                apiURL: this.flexiUrl,
                token: this.flexiToken,
                ref: importResult.data.winstrom.results[0].ref,
                evidence: 'faktura-prijata',
                filename: importedDocument.title || '(dokument bez názvu)',
                note: 'Dokument v Document Manageru',
                link: `${process.env.VUE_APP_DOCUMENT_MANAGER_URL}/documents/${documentId}`,
              });
              this.importResults = {
                ...this.importResults,
                [documentId]: {
                  ...this.importResults[documentId],
                  attachment: { success: true },
                },
              };
            } catch (error) {
              this.importResults = {
                ...this.importResults,
                [documentId]: {
                  ...this.importResults[documentId],
                  attachment: {
                    success: false,
                    error: error.response.data.winstrom.results[0].errors,
                  },
                },
              };
              this.$rollbar.warning('Adding attachment to Flexi failed', error.response.data);
            }
          } catch (error) {
            this.importResults = {
              ...this.importResults,
              [documentId]: {
                invoice: {
                  success: false,
                  error: error.response.data.winstrom.results[0].errors,
                },
              },
            };
            this.$rollbar.info('Importing invoice to Flexi failed', error.response.data);
          }
        };

        await Promise.all(documents.map((documentId) => importDocument(documentId)));

        if (documents.length === importedDocuments.length) {
          let importedString;
          if (importedDocuments.length === 1) {
            importedString = 'Importován';
          } else if (importedDocuments.length >= 2 && importedDocuments.length <= 4) {
            importedString = 'Importovány';
          } else if (importedDocuments.length >= 5) {
            importedString = 'Importováno';
          }

          this.result = `${importedString} ${this.pluralizeDocumentsString(importedDocuments.length, documents.length)}`;
          this.status = 'documents-imported';
        } else {
          this.result = `Nepodařilo se importovat ${this.pluralizeDocumentsString(documents.length - importedDocuments.length)}`;
          this.status = 'something-wrong';
        }
      } catch (error) {
        this.status = 'something-wrong';
        throw error;
      }
    },
    pluralizeDocumentsString(count, totalCount) {
      let plural;
      if (count === 1) {
        plural = `${count} dokument`;
      } else if (count > 1 && count < 5) {
        plural = `${count} dokumenty`;
      } else if (count > 4) {
        plural = `${count} dokumentů`;
      }

      let prefix;
      if (totalCount) {
        if (totalCount === 2) {
          prefix = 'oba ';
        } else if (totalCount >= 3 && count <= 4) {
          prefix = 'všechny ';
        } else if (totalCount >= 5) {
          prefix = 'všech ';
        } else {
          prefix = 'jediný ';
        }
      } else {
        prefix = '';
      }

      return `${prefix}${plural}`;
    },
  },
  watch: {
    documentManagerContainer() {
      this.selectedDocuments = [];
      this.importResults = {};
      this.result = null;
    },
    importResults() {
      this.$emit('import', this.importResults);
    },
  },
  beforeDestroy() {
    this.$emit('import', {});
  },
};
</script>
